import { createApi } from "@reduxjs/toolkit/query/react"
import { authQuery } from "./authQuery"
import {
  ConditionedResultErrorResponse,
  ConditionedResultResponse,
  PostRunGroupParams,
  RunAggregatesResponseDto,
  RunDto,
  RunGroup,
  RunGroupResponse,
  RunResponseDto,
  RunResultsHistoricAggregate,
  RunsNodesHistoricResults,
  getRunAggregates,
  getRunsQueryParams,
} from "@custom-types/modelRun"
import { ThresholdFormValues } from "@features/results/Result/ThresholdChartContainer/ThresholdForm"

export enum RunStatusDto {
  SCHEDULE = "running-schedule",
  CONVERT = "running-convert",
  SAMPLE = "running-sample",
  TRANSFORM = "running-transform",
  INSERT = "running-insert",
  DONE = "done-success",
  ERROR = "done-error",
  TIMEOUT = "done-timeout",
}

export interface RunProgressDto {
  key: string
  status: RunStatusDto
  extra?: {
    message: string
    traceback?: string[] | string
    task?: string
    file?: string
  }
}

export const runApi = createApi({
  reducerPath: "runApi",
  baseQuery: authQuery,
  tagTypes: ["RecentRuns", "Runs"],
  endpoints: (builder) => ({
    getRunResult: builder.query<RunDto, string>({
      query: (key) => ({
        url: `/runs/${key}`,
      }),
    }),
    getRunProgress: builder.query<RunProgressDto, string>({
      query: (key) => ({
        url: `/runs/${key}/progress`,
      }),
    }),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY :(
    getRunValidationReport: builder.query<any, string>({
      query: (key) => ({
        url: `/runs/${key}/validation-report`,
        responseHandler: async (resp) => {
          if (resp.status !== 200) return resp.statusText
          const text = await resp.text()
          return text
        },
      }),
    }),
    getRuns: builder.query<RunResponseDto, getRunsQueryParams>({
      query: ({ pagination__last__key, pagination__limit }) => ({
        url: `/runs`,
        params: {
          pagination__last__key,
          pagination__limit,
        },
      }),
    }),
    getRunAggregates: builder.query<RunAggregatesResponseDto[], getRunAggregates>({
      query: ({ key }) => ({
        url: `/runs/${key}/aggregates`,
      }),
    }),
    getRunGroupsByBaseGraph: builder.query<RunGroupResponse, string>({
      query: (baseGraphKey) => ({
        url: `/run-groups/by-base/${encodeURIComponent(baseGraphKey)}`,
      }),
      providesTags: ["Runs"],
    }),
    getRunsByKeys: builder.query<{ [key: string]: RunDto }, string[]>({
      query: (keys) => {
        const searchParams = new URLSearchParams()
        keys.forEach((key) => searchParams.append("keys", key))

        return { url: `/runs-by-keys?${searchParams.toString()}` }
      },
      providesTags: ["Runs"],
    }),
    postRunGroup: builder.mutation<RunGroup, PostRunGroupParams>({
      query: (params) => {
        const body = new FormData()
        if (params.graph_file_blob) {
          body.append("data", params.graph_file_blob)
        }

        if (params.scenarios.length > 0) {
          params.scenarios.forEach((x) => {
            body.append("scenario_names", x) // this loop is correct for multipart formdata
          })
        } else {
          body.append("scenario_names", "")
        }

        body.append("seed", params.seed.toString())
        body.append("run_name", params.run_name)
        body.append("num_samples", params.number_of_samplings.toString())
        body.append("end_date", params.end_date)
        body.append("description", params.description)
        body.append("validate", params.validate ? "1" : "0")
        body.append("sampling_engine", "EVIEWS")

        if (params.contract_key) body.append("contract_key", params.contract_key)
        else {
          // TODO this should be the contract key chosen by the user [in the case two contracts permit the user to see this base graph]
          //   or _the_ contract which allows the user to see this base graph
          body.append("contract_key", "public")
        }

        return {
          url: `/run-groups`,
          method: "POST",
          formData: true,
          params: {
            base_graph_key: params.base_graph_key,
          },
          body: body,
        }
      },
      invalidatesTags: ["Runs"],
    }),
    getRunGroupsByFamily: builder.query<RunGroupResponse, string>({
      query: (familyKey) => ({
        url: `/run-groups/by-family/${encodeURIComponent(familyKey)}`,
      }),
      providesTags: ["Runs"],
    }),
    getRunGroupByKey: builder.query<RunGroup, string>({
      query: (key) => ({
        url: `/run-groups/${key}`,
      }),
      providesTags: ["Runs"],
    }),
    postConditionedResult: builder.mutation<
      ConditionedResultResponse | ConditionedResultErrorResponse,
      { resultKey: string; condition: ThresholdFormValues }
    >({
      query: (params) => {
        return {
          url: `/conditioned_result`,
          method: "POST",
          params: { key: decodeURIComponent(params.resultKey) },
          body: params.condition,
        }
      },
    }),
    getRunsNodesResultsHistoric: builder.query<
      RunsNodesHistoricResults,
      { runKeys: string[]; nodes: string[] }
    >({
      query: ({ runKeys, nodes }) => {
        const searchParams = new URLSearchParams()
        runKeys.forEach((key) => searchParams.append("keys", key))
        nodes.forEach((node) => searchParams.append("nodes", node))

        return {
          url: `/runs/historic/nodes?${searchParams.toString()}`,
        }
      },
    }),
    getRunsNodesResultsHistoricAggregate: builder.query<
      RunResultsHistoricAggregate,
      { runKeys: string[]; nodes: string[] }
    >({
      query: ({ runKeys, nodes }) => {
        const searchParams = new URLSearchParams()
        runKeys.forEach((key) => searchParams.append("keys", key))
        nodes.forEach((node) => searchParams.append("nodes", node))

        return {
          url: `/runs/historic/aggregate?${searchParams.toString()}`,
        }
      },
    }),
  }),
})

export const {
  useGetRunResultQuery,
  useLazyGetRunResultQuery,
  useGetRunProgressQuery,
  useLazyGetRunValidationReportQuery,
  useGetRunAggregatesQuery,
  usePostRunGroupMutation,
  useLazyGetRunGroupsByFamilyQuery,
  useGetRunGroupsByBaseGraphQuery,
  useGetRunsByKeysQuery,
  usePostConditionedResultMutation,
  useGetRunsNodesResultsHistoricQuery,
  useGetRunsNodesResultsHistoricAggregateQuery,
  useGetRunGroupByKeyQuery,
  useLazyGetRunGroupByKeyQuery,
} = runApi
